export type ContactWithEmailFields = {
  email?: string
  workEmail?: {
    email?: string
    use?: boolean
  }
}

const EMAIL_FORMAT_RE = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export function isValidEmailFormat(email: string | null | undefined): boolean {
  if (!email) {
    return false
  }
  return EMAIL_FORMAT_RE.test(email)
}

export function getSendableEmail(contact: ContactWithEmailFields | null | undefined): string | null {
  if (!contact) {
    return null
  }
  if (contact.workEmail?.use && contact.workEmail?.email) {
    return contact.workEmail.email
  }
  if (contact.email) {
    return contact.email
  }
  return null
}

export function hasSendableEmail(contact: ContactWithEmailFields | null | undefined): boolean {
  const email = getSendableEmail(contact)
  return !!email && email !== ''
}

export function contactForSendMail<T extends ContactWithEmailFields>(contact: T | null | undefined): (T & { email: string }) | null {
  const sendableEmail = getSendableEmail(contact)
  if (!sendableEmail) {
    return null
  }
  return { ...contact, email: sendableEmail }
}

export function recipientEmailForSend(recipient: ContactWithEmailFields | null | undefined): string | null {
  const sendableEmail = getSendableEmail(recipient)
  if (sendableEmail && isValidEmailFormat(sendableEmail)) {
    return sendableEmail
  }
  return null
}
/*
 * Builds recipient entries for every unique, sendable email on a contact,
 * handling both primary and business (`workEmail`) addresses
 */
export function getSendableEmailEntries<T extends ContactWithEmailFields>(contact: T | null | undefined): (T & { email: string })[] {
  if (!contact) {
    return []
  }
  const entries: (T & { email: string })[] = []
  const seen = new Set<string>()
  const addEntry = (email: string | null | undefined, isBusiness: boolean) => {
    if (!email) {
      return
    }
    const normalized = email.trim().toLowerCase()
    if (seen.has(normalized)) {
      return
    }
    seen.add(normalized)
    entries.push({
      ...contact,
      email,
      workEmail: contact.workEmail ? { ...contact.workEmail, use: isBusiness } : contact.workEmail
    } as T & { email: string })
  }
  addEntry(contact.email, false)
  if (contact.workEmail?.use) {
    addEntry(contact.workEmail.email, true)
  }
  return entries
}
